import { useEffect, useMemo, useState } from "react";
import MainNavbar from "../components/reuse/navbar";
import OverviewReportSection from "../components/personal/reports/OverviewReportSection";
import DescriptiveReportSection from "../components/personal/reports/DescriptiveReportSection";
import HypothesesReportSection from "../components/personal/reports/HypothesesReportSection";
import FactorAnalysisReportSection from "../components/personal/reports/FactorAnalysisReportSection";
import SeverityModelsReportSection from "../components/personal/reports/SeverityModelsReportSection";
import FrequencySpatialReportSection from "../components/personal/reports/FrequencySpatialReportSection";
import GeoRiskReportSection from "../components/personal/reports/GeoRiskReportSection";
import GeoClustersReportSection from "../components/personal/reports/GeoClustersReportSection";
import MethodologyReportSection from "../components/personal/reports/MethodologyReportSection";

const REPORT_SECTIONS = [
  {
    id: "overview",
    navLabel: "Общий анализ",
    title: "Общая статистика ДТП",
    description: "Годовые KPI, динамика, сезонность, пострадавшие, погибшие и детские ДТП.",
  },
  {
    id: "descriptive",
    navLabel: "Описательная",
    title: "Описательная статистика",
    description: "Виды ДТП, территории, элементы улично-дорожной сети, время и индексы тяжести.",
  },
  {
    id: "hypotheses",
    navLabel: "Гипотезы",
    title: "Статистические гипотезы",
    description: "Проверенные связи, уровень статистической значимости и содержательные выводы.",
  },
  {
    id: "factor-analysis",
    navLabel: "Факторный анализ",
    title: "Факторный анализ",
    description: "Отбор признаков, диагностика факторизуемости и интерпретация погодных факторов.",
  },
  {
    id: "severity-models",
    navLabel: "Тяжесть ДТП",
    title: "Модели тяжести ДТП",
    description: "Сравнение моделей, калибровка и устойчивые ассоциации факторов с тяжестью последствий.",
  },
  {
    id: "frequency-spatial",
    navLabel: "Частота и пространство",
    title: "Частота ДТП и пространственный анализ",
    description: "Модели частоты, пространственная валидация, риск-классы и инфраструктурные факторы.",
  },
  {
    id: "geo-risk",
    navLabel: "Карта риска",
    title: "Карта прогнозного риска",
    description: "Прогнозное число ДТП и концентрация риска по H3-гексагонам за 2024 год.",
  },
  {
    id: "geo-clusters",
    navLabel: "Кластеры",
    title: "Пространственные кластеры",
    description: "Значимые Local Moran-кластеры и их интерпретация.",
  },
  {
    id: "methodology",
    navLabel: "Методология",
    title: "Методология и качество данных",
    description: "Аналитические совокупности, контрольные значения, ограничения и правила интерпретации.",
  },
];

function getInitialSectionId() {
  const sectionId = window.location.hash.replace(/^#/, "");
  return REPORT_SECTIONS.some((section) => section.id === sectionId) ? sectionId : "overview";
}

export default function ReportsPage() {
  const [activeSectionId, setActiveSectionId] = useState(getInitialSectionId);
  const activeSection = useMemo(
    () => REPORT_SECTIONS.find((section) => section.id === activeSectionId) || REPORT_SECTIONS[0],
    [activeSectionId]
  );

  useEffect(() => {
    const syncSectionFromHash = () => setActiveSectionId(getInitialSectionId());
    window.addEventListener("hashchange", syncSectionFromHash);
    return () => window.removeEventListener("hashchange", syncSectionFromHash);
  }, []);

  const selectSection = (sectionId) => {
    if (sectionId === activeSectionId) return;
    window.location.hash = sectionId;
  };

  return (
    <>
      <MainNavbar />
      <main className="statistics-report-page">
        <section className="statistics-report-heading" aria-labelledby="statistics-report-title">
          <div>
            <p className="statistics-report-eyebrow">Статистический отчёт</p>
            <h1 id="statistics-report-title">Анализ ДТП Москвы</h1>
          </div>
          <p className="statistics-report-period">Готовый аналитический срез · 2020–2024</p>
        </section>

        <nav className="statistics-report-navigation" aria-label="Разделы статистического отчёта">
          <div className="statistics-report-navigation-inner">
            {REPORT_SECTIONS.map((section) => {
              const selected = section.id === activeSection.id;
              return (
                <button
                  key={section.id}
                  id={`report-section-tab-${section.id}`}
                  className={`statistics-report-navigation-item${selected ? " is-active" : ""}`}
                  type="button"
                  aria-current={selected ? "page" : undefined}
                  onClick={() => selectSection(section.id)}
                >
                  {section.navLabel}
                </button>
              );
            })}
          </div>
        </nav>

        <section
          id={`report-section-panel-${activeSection.id}`}
          className="statistics-report-content-shell"
          aria-labelledby={`report-section-tab-${activeSection.id}`}
        >
          {activeSection.id !== "descriptive" && activeSection.id !== "hypotheses" && activeSection.id !== "severity-models" && activeSection.id !== "frequency-spatial" && activeSection.id !== "geo-risk" && activeSection.id !== "geo-clusters" && activeSection.id !== "methodology" ? (
            <div className="statistics-report-content-intro">
              <p className="statistics-report-section-number">
                {String(REPORT_SECTIONS.indexOf(activeSection) + 1).padStart(2, "0")}. {activeSection.navLabel}
              </p>
              <h2>{activeSection.title}</h2>
              <p>{activeSection.description}</p>
            </div>
          ) : null}

          {activeSection.id === "overview" ? <OverviewReportSection /> : activeSection.id === "descriptive" ? <DescriptiveReportSection /> : activeSection.id === "hypotheses" ? <HypothesesReportSection /> : activeSection.id === "factor-analysis" ? <FactorAnalysisReportSection /> : activeSection.id === "severity-models" ? <SeverityModelsReportSection /> : activeSection.id === "frequency-spatial" ? <FrequencySpatialReportSection /> : activeSection.id === "geo-risk" ? <GeoRiskReportSection /> : activeSection.id === "geo-clusters" ? <GeoClustersReportSection /> : activeSection.id === "methodology" ? <MethodologyReportSection /> : (
            <div className="statistics-report-placeholder" aria-live="polite">
              <div className="statistics-report-placeholder-icon" aria-hidden="true">▦</div>
              <div>
                <h3>Раздел подготовлен к переносу</h3>
                <p>
                  На следующем этапе здесь появятся графики, таблицы и пояснения из аналитического отчёта.
                  В этой итерации добавлены только навигация и общий каркас страницы.
                </p>
              </div>
            </div>
          )}
        </section>
      </main>
    </>
  );
}
